import React from 'react';
import Typography from '@mui/material/Typography';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';

import PopoverOptions from 'pages/BuoyData/SideDrawer/PopoverOptions';
import useViewportRatio from 'utils/useViewportRatio';

import KeyboardArrowDownSharpIcon from '@mui/icons-material/KeyboardArrowDownSharp';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';

import { useAppDispatch } from 'redux/hooks';
import {
  deleteCard,
  updateCardDepth
} from 'redux/slice/datasetCardControllerSlice';

import custom_palettes from 'theme/custom_palettes';
import custom_typographys from 'theme/custom_typographys';
import { deleteDataset } from 'redux/slice/graphControllerSlice';

// =============================================================================
const MOBILE_POPOVER_POSITION_FROM_TOP = 240;
const POPOVER_POSITION_FROM_TOP = 500;
const POPOVER_POSITION_FROM_LEFT = 400;
// =============================================================================
const set_card_wrapper_style: any = [
  { border: `2px solid ${custom_palettes.blue[800]}` },
  { border: `2px solid ${custom_palettes.green[500]}` }
];

const title_wrapper_style: any = [
  { backgroundColor: custom_palettes.blue[50] },
  { backgroundColor: custom_palettes.green[50] }
];

const title_style: any = [
  { color: custom_palettes.blue[800] },
  { color: custom_palettes.green[500] }
];

const custom_select_wrapper_style = {
  width: '90%',
  margin: '0 auto',
  // border: `1px solid ${custom_palettes.gray[100]}`,
  borderRadius: '24px',
  marginBottom: '12px',
  ...custom_typographys.body_regular,
  color: custom_palettes.blue[900],
  padding: '0 16px'
};

// =============================================================================
interface SetCardProps {
  index: string;
  location: string;
  depths: Array<Record<string, string>>;
  depth: Record<string, string>;
  parameter: string;
  mobile?: boolean;
}

export default function SetCard(props: SetCardProps) {
  return (
    <div
      style={{
        width: '100%',
        borderRadius: '16px',
        margin: '8px auto',
        maxWidth: '90%',
        ...set_card_wrapper_style[props.index]
      }}
    >
      <Header index={props.index} />
      <CustomForm
        index={props.index}
        location={props.location}
        depths={props.depths}
        depth={props.depth}
        parameter={props.parameter}
        mobile={props.mobile ?? false}
      />
    </div>
  );
}

// =============================================================================
interface HeaderProps {
  index: string;
}

const Header = (props: HeaderProps) => {
  const dispatch = useAppDispatch();

  return (
    <div
      style={{
        height: 40,
        display: 'flex',
        alignItems: 'center',
        paddingLeft: '16px',
        borderRadius: '16px 16px 0px 0px',
        marginBottom: '8px',
        ...title_wrapper_style[props.index]
      }}
    >
      <Typography
        variant='body_regular_strong'
        style={title_style[props.index]}
      >
        SET {parseInt(props.index) + 1}
      </Typography>
      <div style={{ flex: 1 }} />
      <IconButton onClick={deleteCardOnClick} style={{ marginRight: 8 }}>
        <CloseOutlinedIcon />
      </IconButton>
    </div>
  );

  function deleteCardOnClick() {
    dispatch(deleteDataset(props.index));
    dispatch(deleteCard(props.index));
  }
};

// =============================================================================
interface CustomFormProps {
  index: string;
  location: string;
  depths: Array<Record<string, string>>;
  depth: Record<string, string>;
  parameter: string;
  mobile: boolean;
}

const CustomForm = (props: CustomFormProps) => {
  const dispatch = useAppDispatch();

  const handleDepthChange = (e: any) => {
    dispatch(updateCardDepth({ index: props.index, data: e.target.value }));
  };

  return (
    <div>
      <CustomParameterSelect
        index={props.index}
        value={props.location}
        location={props.location}
        mobile={props.mobile}
      />

      <CustomSelect
        defaultValue={props.depths[0].text}
        value={props.depth.value}
        menu={props.depths}
        onChange={handleDepthChange}
      />

      <CustomParameterSelect
        index={props.index}
        value={props.parameter}
        location={props.location}
        mobile={props.mobile}
      />
    </div>
  );
};

// =============================================================================
interface CustomSelectProps {
  defaultValue: string;
  value: string;
  menu: Array<Record<string, string>>;
  onChange?: (event: SelectChangeEvent) => void;
}

const CustomSelect = (props: CustomSelectProps) => {
  return (
    <FormControl fullWidth>
      <Select
        defaultValue={props.defaultValue}
        onChange={props.onChange}
        value={props.value}
        sx={{
          ...custom_select_wrapper_style,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: custom_palettes.gray[100]
          }
        }}
        IconComponent={() => <KeyboardArrowDownSharpIcon />}
      >
        {props.menu.map((item: Record<string, string>, index: number) => (
          <MenuItem key={index} value={item.value}>
            {item.text}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

// =============================================================================
const parameter_select_wrapper_style = {
  display: 'flex',
  width: '90%',
  margin: 'auto',
  height: 50,
  border: `1px solid ${custom_palettes.gray[100]}`,
  borderRadius: 24,
  alignItems: 'center',
  marginBottom: 12
};

type CustomParameterSelectProps = {
  location: string;
  value: string;
  index: string;
  mobile?: boolean;
};

const CustomParameterSelect = (props: CustomParameterSelectProps) => {
  const [SelectedCardIndex, setSelectedCardIndex] = React.useState<any>(null);
  const [anchorEl, setAnchorEl] = React.useState(null);
  const ratio = useViewportRatio(1440);

  return (
    <>
      <PopoverOptions
        anchorEl={anchorEl}
        onClose={handleClose}
        setAnchorEl={setAnchorEl}
        anchorPosition={{
          top: props.mobile
            ? MOBILE_POPOVER_POSITION_FROM_TOP
            : POPOVER_POSITION_FROM_TOP,
          left: POPOVER_POSITION_FROM_LEFT * ratio
        }}
        mode='updateCard'
        cardIndex={SelectedCardIndex}
        location={props.location}
        mobile={props.mobile}
      />
      <div style={parameter_select_wrapper_style} onClick={handleClick}>
        <Typography
          variant='body_regular'
          color='blue.900'
          style={{
            marginLeft: 32,
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {props.value}
        </Typography>
        <div style={{ flex: 1 }} />
        <KeyboardArrowDownSharpIcon
          style={{ marginRight: 16, color: custom_palettes.blue[900] }}
        />
      </div>
    </>
  );

  function handleClick(event: any) {
    setSelectedCardIndex(props.index);
    setAnchorEl(event.currentTarget);
  }

  function handleClose() {
    setAnchorEl(null);
  }
};
